/**
 * Classe per la gestione avanzata dei filtri di ricerca veicoli
 * Gestisce dropdown dinamici, filtri AJAX e sincronizzazione stato
 */

export default class AdvancedFormFilter {
    /**
     * Costruttore della classe
     * @param {Object} config - Configurazione dell'istanza
     * @param {string} config.formId - ID del form
     * @param {string} config.apiUrl - URL endpoint AJAX
     * @param {Object} config.selectedValues - Valori iniziali selezionati
     * @param {Object} config.placeholders - Testi placeholder per le dropdown
     */
    constructor(config) {
        // Configurazione obbligatoria
        this.formId = config.formId;
        this.apiUrl = config.apiUrl;
        this.selectedValues = config.selectedValues || {};

        // Configurazione opzionale con valori di default
        this.placeholders = {
            bodytype: "Carrozzeria",
            brand: "Marca",
            serie: "Serie",
            model: "Modello",
            fuel: "Alimentazione",
            gear: "Cambio",
            ...config.placeholders,
        };

        // Inizializzazione stati e flag
        this.initialLoad = true;

        // Cache elementi DOM
        this.formSearch = null;
        this.types = null;
        this.warrantiesCheckboxes = null;
        this.groupCheckboxes = null;
        this.resetBtn = null;

        // Configurazione struttura dropdown
        this.selectOrder = [
            `${this.formId}select-brand`,
            `${this.formId}select-serie`,
            `${this.formId}select-model`,
            `${this.formId}select-bodytype`,
            `${this.formId}select-fuel`,
            `${this.formId}select-gear`,
            `${this.formId}price_min`,
            `${this.formId}price_max`,
            `${this.formId}km_min`,
            `${this.formId}km_max`,
        ];

        this.selectKeyMap = {
            [`${this.formId}select-bodytype`]: "bodytype",
            [`${this.formId}select-brand`]: "brand",
            [`${this.formId}select-serie`]: "serie",
            [`${this.formId}select-model`]: "model",
            [`${this.formId}select-fuel`]: "fuel",
            [`${this.formId}select-gear`]: "gear",
        };

        // Mappature per le dropdown principali
        this.dropdownMappings = [
            {
                key: "bodytype",
                idSuffix: "select-bodytype",
                placeholder: this.placeholders.bodytype,
            },
            {
                key: "brand",
                idSuffix: "select-brand",
                placeholder: this.placeholders.brand,
            },
            {
                key: "serie",
                idSuffix: "select-serie",
                placeholder: this.placeholders.serie,
            },
            {
                key: "model",
                idSuffix: "select-model",
                placeholder: this.placeholders.model,
            },
            {
                key: "fuel",
                idSuffix: "select-fuel",
                placeholder: this.placeholders.fuel,
            },
            {
                key: "gear",
                idSuffix: "select-gear",
                placeholder: this.placeholders.gear,
            },
        ];

        // Binding contesto per i metodi
        this.handleTypeChange = this.handleTypeChange.bind(this);
        this.updateFilters = this.updateFilters.bind(this);
        this.debouncedUpdateFilters = this.debounce(this.updateFilters, 500);
        this.resetFormFilters = this.resetFormFilters.bind(this);
        this.resetDependentSelects = this.resetDependentSelects.bind(this);

        // Avvia inizializzazione
        this.init();
    }

    /**
     * Inizializza la classe - setup DOM e event listeners
     */
    init() {
        this.cacheDOMElements();
        this.setupEventListeners();
        this.initializeFormState();

        // Avvia il primo caricamento dei filtri
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () => {
                this.handleTypeChange();
                this.updateFilters();
            });
        } else {
            this.handleTypeChange();
            this.updateFilters();
        }
    }

    /**
     * Cache degli elementi DOM frequentemente utilizzati
     */
    cacheDOMElements() {
        this.formSearch = document.getElementById(this.formId);
        this.types = document.querySelectorAll('input[name="type_name[]"]');
        this.warrantiesCheckboxes = document.querySelectorAll(
            `input[name="warranties[]"]`,
        );
        this.groupCheckboxes = document.querySelectorAll(
            `input[name="gruppi[]"]`,
        );
        this.resetBtn = document.querySelector(`#${this.formId}form_reset`);
    }

    /**
     * Setup di tutti gli event listeners
     */
    setupEventListeners() {
        this.setupTypeEventListeners();
        this.setupCheckboxEventListeners();
        this.setupSelectEventListeners();
        this.setupResetEventListener();
    }

    /**
     * Inizializza lo stato del form con i valori predefiniti
     */
    initializeFormState() {
        // Imposta i valori iniziali delle select se presenti
        this.dropdownMappings.forEach((mapping) => {
            const select = document.getElementById(
                `${this.formId}${mapping.idSuffix}`,
            );
            if (select && this.selectedValues[mapping.key]) {
                select.value = this.selectedValues[mapping.key];
            }
        });

        // Imposta i valori iniziali per prezzo e km
        const priceMinSelect = document.getElementById(
            `${this.formId}price_min`,
        );
        const priceMaxSelect = document.getElementById(
            `${this.formId}price_max`,
        );
        const kmMinSelect = document.getElementById(`${this.formId}km_min`);
        const kmMaxSelect = document.getElementById(`${this.formId}km_max`);

        if (priceMinSelect && this.selectedValues.price_min)
            priceMinSelect.value = this.selectedValues.price_min;
        if (priceMaxSelect && this.selectedValues.price_max)
            priceMaxSelect.value = this.selectedValues.price_max;
        if (kmMinSelect && this.selectedValues.km_min)
            kmMinSelect.value = this.selectedValues.km_min;
        if (kmMaxSelect && this.selectedValues.km_max)
            kmMaxSelect.value = this.selectedValues.km_max;
    }

    /**
     * Setup event listeners per i checkbox tipo veicolo
     */
    setupTypeEventListeners() {
        this.types.forEach((typeCheckbox) => {
            typeCheckbox.addEventListener("change", () => {
                this.handleTypeChange(typeCheckbox);
            });
        });
    }

    /**
     * Setup event listeners per checkbox groups e warranties
     */
    setupCheckboxEventListeners() {
        // Checkbox gruppi
        document.querySelectorAll(`input[name="gruppi[]"]`).forEach((input) => {
            input.addEventListener("change", this.debouncedUpdateFilters);
        });

        // Checkbox warranties e types
        document
            .querySelectorAll(
                `input[name="type_name[]"], input[name="gruppi[]"], input[name="warranties[]"]`,
            )
            .forEach((input) => {
                input.addEventListener("change", this.debouncedUpdateFilters);
            });
    }

    /**
     * Setup event listeners per tutte le select
     */
    setupSelectEventListeners() {
        // Select prezzo e km (invariato)
        document
            .querySelectorAll(
                `#${this.formId}price_min, #${this.formId}price_max, #${this.formId}km_min, #${this.formId}km_max`,
            )
            .forEach((select) => {
                select.addEventListener("change", (e) => {
                    const keyMap = {
                        [`${this.formId}price_min`]: "price_min",
                        [`${this.formId}price_max`]: "price_max",
                        [`${this.formId}km_min`]: "km_min",
                        [`${this.formId}km_max`]: "km_max",
                    };
                    this.selectedValues[keyMap[e.target.id]] = e.target.value;
                });
            });

        // Select principali con dipendenze
        document
            .querySelectorAll(
                `#${this.formId}select-bodytype, #${this.formId}select-brand, #${this.formId}select-model, #${this.formId}select-fuel, #${this.formId}select-gear, #${this.formId}price_min, #${this.formId}price_max, #${this.formId}km_min, #${this.formId}km_max`,
            )
            .forEach((select) => {
                select.addEventListener("change", this.debouncedUpdateFilters);
            });

        // Gestione reset select dipendenti
        this.selectOrder.forEach((id) => {
            const selectElem = document.getElementById(id);
            if (selectElem) {
                selectElem.addEventListener("change", (e) => {
                    this.resetDependentSelects(e.target.id);
                    this.debouncedUpdateFilters();
                });
            }
        });

        // Select principali con aggiornamento valori
        document
            .querySelectorAll(
                `#${this.formId}select-bodytype, #${this.formId}select-brand, #${this.formId}select-serie, #${this.formId}select-model, #${this.formId}select-fuel, #${this.formId}select-gear`,
            )
            .forEach((select) => {
                select.addEventListener("change", (e) => {
                    const keyMap = {
                        [`${this.formId}select-bodytype`]: "bodytype",
                        [`${this.formId}select-brand`]: "brand",
                        [`${this.formId}select-model`]: "model",
                        [`${this.formId}select-serie`]: "serie",
                        [`${this.formId}select-fuel`]: "fuel",
                        [`${this.formId}select-gear`]: "gear",
                    };

                    this.selectedValues[keyMap[e.target.id]] = e.target.value;

                    // RESET LOGICA PER BRAND (come carrozzeria)
                    // Se cambia brand, resetta model, serie, fuel, gear e range numerici
                    if (e.target.id === `${this.formId}select-brand`) {
                        // Resetta model
                        const modelSelect = document.getElementById(
                            `${this.formId}select-model`,
                        );
                        if (modelSelect) {
                            modelSelect.value = "";
                            this.selectedValues.model = "";
                        }

                        // Resetta serie
                        const serieSelect = document.getElementById(
                            `${this.formId}select-serie`,
                        );
                        if (serieSelect) {
                            serieSelect.value = "";
                            this.selectedValues.serie = "";
                        }

                        // Resetta fuel
                        const fuelSelect = document.getElementById(
                            `${this.formId}select-fuel`,
                        );
                        if (fuelSelect) {
                            fuelSelect.value = "";
                            this.selectedValues.fuel = "";
                        }

                        // Resetta gear
                        const gearSelect = document.getElementById(
                            `${this.formId}select-gear`,
                        );
                        if (gearSelect) {
                            gearSelect.value = "";
                            this.selectedValues.gear = "";
                        }

                        // Resetta range prezzo
                        const priceMin = document.getElementById(
                            `${this.formId}price_min`,
                        );
                        const priceMax = document.getElementById(
                            `${this.formId}price_max`,
                        );
                        if (priceMin) {
                            priceMin.value = "";
                            this.selectedValues.price_min = "";
                        }
                        if (priceMax) {
                            priceMax.value = "";
                            this.selectedValues.price_max = "";
                        }

                        // Resetta range km
                        const kmMin = document.getElementById(
                            `${this.formId}km_min`,
                        );
                        const kmMax = document.getElementById(
                            `${this.formId}km_max`,
                        );
                        if (kmMin) {
                            kmMin.value = "";
                            this.selectedValues.km_min = "";
                        }
                        if (kmMax) {
                            kmMax.value = "";
                            this.selectedValues.km_max = "";
                        }
                    }

                    this.resetDependentSelects(e.target.id);
                    this.debouncedUpdateFilters();
                });
            });
    }

    /**
     * Setup event listener per reset form
     */
    setupResetEventListener() {
        // Reset mobile (già esistente)
        const resetMobile = document.querySelector(`#${this.formId}form_reset`);
        if (resetMobile) {
            resetMobile.addEventListener("click", this.resetFormFilters);
        }

        // Reset desktop (nuovo)
        const resetDesktop = document.querySelector(
            `#${this.formId}form_reset_desktop`,
        );
        if (resetDesktop) {
            resetDesktop.addEventListener("click", this.resetFormFilters);
        }
    }

    /**
     * Gestisce il cambio tipo veicolo (Auto/Veicoli)
     * @param {HTMLElement} typeCheckbox - Checkbox cambiato
     */
    handleTypeChange(typeCheckbox = null) {
        const selectedType =
            typeCheckbox || [...this.types].find((t) => t.checked);

        if (selectedType && selectedType.checked) {
            // Deseleziona altri tipi
            this.types.forEach((other) => {
                if (other !== selectedType) other.checked = false;
            });

            // Se è Veicoli (value=3) disattiva warranties
            if (selectedType.value == "3") {
                this.warrantiesCheckboxes.forEach((w) => {
                    w.checked = false;
                    w.disabled = true;
                });
            } else {
                // Se è Auto, abilita warranties
                this.warrantiesCheckboxes.forEach((w) => (w.disabled = false));
            }
        } else {
            // Nessun tipo selezionato: riabilita warranties
            this.warrantiesCheckboxes.forEach((w) => (w.disabled = false));
        }
    }

    /**
     * Aggiorna i filtri via AJAX
     */
    async updateFilters() {
        try {
            this.toggleSelectsState(true);

            const formData = this.collectFormData();
            const response = await this.makeAjaxRequest(formData);

            this.updateDropdowns(response.data);
            this.updateWarrantiesState(response.data, formData.selectedGroups);
        } catch (error) {
            console.error("Errore durante l'aggiornamento dei filtri:", error);
            this.updateCount("Errore");
        } finally {
            this.toggleSelectsState(false);
            if (this.initialLoad) {
                this.initialLoad = false;
            }
        }
    }

    /**
     * Raccoglie i dati dal form per la richiesta AJAX
     * @returns {Object} Dati del form
     */
    collectFormData() {
        const typeCheckboxes = document.querySelectorAll(
            `input[name="type_name[]"]`,
        );
        const selectedTypes = Array.from(typeCheckboxes)
            .filter((cb) => cb.checked)
            .map((cb) => cb.value);

        const selectedGroups = Array.from(this.groupCheckboxes)
            .filter((cb) => cb.checked)
            .map((cb) => cb.value);

        const selectedWarranties = Array.from(this.warrantiesCheckboxes)
            .filter((cb) => cb.checked && !cb.disabled)
            .map((cb) => cb.value);

        return {
            selectedTypes,
            selectedGroups,
            selectedWarranties,
            selectedBodyType: this.getSelectValue(
                "select-bodytype",
                "bodytype",
            ),
            selectedBrand: this.getSelectValue("select-brand", "brand"),
            selectedModel: this.getSelectValue("select-model", "model"),
            selectedFuel: this.getSelectValue("select-fuel", "fuel"),
            selectedGear: this.getSelectValue("select-gear", "gear"),
            selectedSerie: this.getSelectValue("select-serie", "serie"),
            minPrice: this.getSelectValue("price_min", "price_min"),
            maxPrice: this.getSelectValue("price_max", "price_max"),
            minKm: this.getSelectValue("km_min", "km_min"),
            maxKm: this.getSelectValue("km_max", "km_max"),
        };
    }

    /**
     * Ottiene il valore di una select considerando il caricamento iniziale
     * @param {string} elementId - ID dell'elemento senza formId
     * @param {string} valueKey - Chiave in selectedValues
     * @returns {string} Valore della select
     */
    getSelectValue(elementId, valueKey) {
        return this.initialLoad && this.selectedValues[valueKey]
            ? this.selectedValues[valueKey]
            : document.getElementById(`${this.formId}${elementId}`)?.value ||
                  "";
    }

    /**
     * Esegue la richiesta AJAX
     * @param {Object} formData - Dati del form
     * @returns {Promise} Promise della risposta
     */
    async makeAjaxRequest(formData) {
        const payload = {
            type_name: formData.selectedTypes,
            group_name: formData.selectedGroups,
            warranties_name: formData.selectedWarranties,
            bodytype_name: formData.selectedBodyType,
            brand_name: formData.selectedBrand,
            model_name: formData.selectedModel,
            serie_name: formData.selectedSerie,
            fuel_name: formData.selectedFuel,
            gear_name: formData.selectedGear,
            min_price: formData.minPrice,
            max_price: formData.maxPrice,
            min_km: formData.minKm,
            max_km: formData.maxKm,
        };

        return await axios.post(this.apiUrl, payload, {
            headers: {
                "X-Requested-With": "XMLHttpRequest",
            },
            timeout: 8000,
        });
    }

    /**
     * Aggiorna lo stato delle warranties in base ai gruppi selezionati
     * @param {Object} responseData - Dati dalla risposta AJAX
     * @param {Array} selectedGroups - Gruppi selezionati
     */
    updateWarrantiesState(responseData, selectedGroups) {
        const anyGroupSelected = selectedGroups.length > 0;

        if (
            anyGroupSelected &&
            responseData.warranties &&
            responseData.warranties.filtered
        ) {
            const serverIds = (responseData.warranties.all ?? []).map((w) =>
                String(w.id),
            );

            const idsEnabled = new Set([...serverIds, ...selectedGroups]);

            this.warrantiesCheckboxes.forEach((cb) => {
                if (idsEnabled.has(cb.value)) {
                    cb.disabled = false;
                } else {
                    cb.checked = false;
                    cb.disabled = true;
                }
            });
        } else {
            this.warrantiesCheckboxes.forEach((cb) => {
                cb.disabled = false;
            });
        }
    }

    /**
     * Aggiorna tutte le dropdown con i nuovi dati
     * @param {Object} data - Dati dalla risposta AJAX
     */
    updateDropdowns(data) {
        let count = 0;

        // Aggiorna dropdown principali
        this.dropdownMappings.forEach((mapping) => {
            count = this.updateSingleDropdown(mapping, data, count);
        });

        // Aggiorna dropdown prezzo e km
        if (data.price_range) {
            this.updatePriceDropdowns(data.price_range);
        }

        if (data.km_range) {
            this.updateKmDropdowns(data.km_range);
        }

        // Aggiorna conteggio
        this.updateCountDisplay(data, count);
    }

    /**
     * Aggiorna una singola dropdown
     * @param {Object} mapping - Configurazione dropdown
     * @param {Object} data - Dati risposta
     * @param {number} count - Contatore corrente
     * @returns {number} Nuovo conteggio
     */
    updateSingleDropdown(mapping, data, count) {
        const select = document.getElementById(
            `${this.formId}${mapping.idSuffix}`,
        );
        if (!select) return count;

        const currentValue = select.value;
        select.innerHTML = `<option value="">${mapping.placeholder}</option>`;

        let filteredOptions =
            (data[mapping.key] && data[mapping.key].filtered) || [];
        let allOptions = (data[mapping.key] && data[mapping.key].all) || [];

        // Filtra e ordina opzioni
        filteredOptions = filteredOptions.filter((item) => item.name != null);
        filteredOptions.sort((a, b) => {
            const aSelected =
                this.selectedValues[mapping.key] &&
                this.selectedValues[mapping.key] !== "" &&
                (a.id == this.selectedValues[mapping.key] ||
                    a.slug == this.selectedValues[mapping.key]);
            const bSelected =
                this.selectedValues[mapping.key] &&
                this.selectedValues[mapping.key] !== "" &&
                (b.id == this.selectedValues[mapping.key] ||
                    b.slug == this.selectedValues[mapping.key]);
            if (aSelected && !bSelected) return -1;
            if (!aSelected && bSelected) return 1;
            return a.name.localeCompare(b.name);
        });

        let foundSelected = false;
        let temporaryCount = 0;

        // Popola le opzioni
        allOptions.forEach((item) => {
            if (!item.slug) return;

            const option = document.createElement("option");
            option.value = item.slug;
            option.textContent = `${item.name} (${item.total})`;

            const isSelected = this.isOptionSelected(
                item,
                mapping.key,
                currentValue,
            );
            if (isSelected) {
                option.selected = true;
                count = item.total;
                foundSelected = true;
            } else if (mapping.key === "brand") {
                temporaryCount += item.total;
            }

            select.appendChild(option);
        });

        // Se nessuna opzione selezionata per i brand, usa il totale accumulato
        if (!foundSelected && mapping.key === "brand") {
            count = temporaryCount;
        }

        return count;
    }

    /**
     * Verifica se un'opzione è selezionata
     * @param {Object} item - Opzione
     * @param {string} key - Chiave mapping
     * @param {string} currentValue - Valore corrente
     * @returns {boolean} True se selezionata
     */
    isOptionSelected(item, key, currentValue) {
        return (
            (this.selectedValues[key] &&
                this.selectedValues[key] !== "" &&
                (item.id == this.selectedValues[key] ||
                    item.slug == this.selectedValues[key])) ||
            (currentValue !== "" && item.slug == currentValue)
        );
    }

    /**
     * Aggiorna le dropdown del prezzo
     * @param {Object} priceRange - Dati range prezzi
     */
    updatePriceDropdowns(priceRange) {
        const priceFromSelect = document.getElementById(
            `${this.formId}price_min`,
        );
        const priceToSelect = document.getElementById(
            `${this.formId}price_max`,
        );

        if (!priceFromSelect || !priceToSelect) return;

        const currentPriceFrom = this.getCurrentValue(
            "price_min",
            priceFromSelect.value,
        );
        const currentPriceTo = this.getCurrentValue(
            "price_max",
            priceToSelect.value,
        );

        this.populatePriceDropdowns(
            priceFromSelect,
            priceToSelect,
            priceRange,
            currentPriceFrom,
            currentPriceTo,
        );
    }

    /**
     * Aggiorna le dropdown dei km
     * @param {Object} kmRange - Dati range km
     */
    updateKmDropdowns(kmRange) {
        const kmFromSelect = document.getElementById(`${this.formId}km_min`);
        const kmToSelect = document.getElementById(`${this.formId}km_max`);

        if (!kmFromSelect || !kmToSelect) return;

        const currentKmFrom = this.getCurrentValue(
            "km_min",
            kmFromSelect.value,
        );
        const currentKmTo = this.getCurrentValue("km_max", kmToSelect.value);

        this.populateKmDropdowns(
            kmFromSelect,
            kmToSelect,
            kmRange,
            currentKmFrom,
            currentKmTo,
        );
    }

    /**
     * Ottiene il valore corrente considerando il caricamento iniziale
     * @param {string} key - Chiave selectedValues
     * @param {string} currentValue - Valore corrente
     * @returns {string} Valore appropriato
     */
    getCurrentValue(key, currentValue) {
        return this.initialLoad && this.selectedValues[key]
            ? this.selectedValues[key]
            : currentValue;
    }

    /**
     * Popola le dropdown del prezzo
     */
    /**
     * Popola le dropdown del prezzo
     */
    populatePriceDropdowns(
        fromSelect,
        toSelect,
        priceRange,
        currentFrom,
        currentTo,
    ) {
        fromSelect.innerHTML = `<option value="">Da</option>`;
        toSelect.innerHTML = `<option value="">A</option>`;

        let minPrice = Math.floor(priceRange.min_price / 5000) * 5000;
        let maxPrice = Math.ceil(priceRange.max_price / 5000) * 5000;

        // Se minPrice è 0, inizia da 0 ma non mostrarlo come opzione
        // Iniziamo da 5000 se minPrice è 0
        let startPrice = minPrice === 0 ? 5000 : minPrice;

        // Popola select "Da" - evita di mostrare 0
        for (let price = startPrice; price < maxPrice; price += 5000) {
            const option = document.createElement("option");
            option.value = price;
            option.textContent = `€ ${price.toLocaleString()}`;
            if (currentFrom == price) option.selected = true;
            fromSelect.appendChild(option);
        }

        // Popola select "A"
        let startPriceTo =
            currentFrom && currentFrom !== ""
                ? parseInt(currentFrom) + 5000
                : minPrice === 0
                  ? 10000
                  : minPrice + 5000;

        if (startPriceTo > maxPrice) startPriceTo = maxPrice;
        if (startPriceTo < (minPrice === 0 ? 5000 : minPrice + 5000)) {
            startPriceTo = minPrice === 0 ? 10000 : minPrice + 5000;
        }

        for (let price = startPriceTo; price <= maxPrice; price += 5000) {
            const option = document.createElement("option");
            option.value = price;
            option.textContent = `€ ${price.toLocaleString()}`;
            if (currentTo == price) option.selected = true;
            toSelect.appendChild(option);
        }

        this.enableSelects([fromSelect, toSelect]);
    }

    /**
     * Popola le dropdown dei km
     */
    populateKmDropdowns(fromSelect, toSelect, kmRange, currentFrom, currentTo) {
        fromSelect.innerHTML = `<option value="">Da</option>`;
        toSelect.innerHTML = `<option value="">A</option>`;

        const minKm = Math.floor(kmRange.min_km / 5000) * 5000;
        const maxKm = Math.ceil(kmRange.max_km / 5000) * 5000;

        // Popola select "Da"
        for (let km = minKm; km < maxKm; km += 5000) {
            const option = document.createElement("option");
            option.value = km;
            option.textContent = `${km.toLocaleString()} km`;
            if (currentFrom !== "" && currentFrom == km) option.selected = true;
            fromSelect.appendChild(option);
        }

        // Popola select "A"
        for (let km = minKm + 5000; km <= maxKm; km += 5000) {
            const option = document.createElement("option");
            option.value = km;
            option.textContent = `${km.toLocaleString()} km`;
            if (currentTo !== "" && currentTo == km) option.selected = true;
            toSelect.appendChild(option);
        }

        this.enableSelects([fromSelect, toSelect]);
    }

    /**
     * Abilita e pulisce le classi delle select
     * @param {Array} selects - Array di elementi select
     */
    enableSelects(selects) {
        selects.forEach((select) => {
            if (select) {
                select.disabled = false;
                select.classList.remove("loading");
            }
        });
    }

    /**
     * Aggiorna il display del conteggio
     * @param {Object} data - Dati risposta
     * @param {number} count - Conteggio base
     */
    updateCountDisplay(data, count) {
        let countFound = false;

        const selectedPriceFrom = this.getCurrentValue(
            "price_min",
            document.getElementById(`${this.formId}price_min`)?.value,
        );
        const selectedPriceTo = this.getCurrentValue(
            "price_max",
            document.getElementById(`${this.formId}price_max`)?.value,
        );
        const selectedKmFrom = this.getCurrentValue(
            "km_min",
            document.getElementById(`${this.formId}km_min`)?.value,
        );
        const selectedKmTo = this.getCurrentValue(
            "km_max",
            document.getElementById(`${this.formId}km_max`)?.value,
        );

        // Priorità 1: conteggio per prezzo
        if (
            (selectedPriceFrom &&
                selectedPriceFrom !== "" &&
                selectedPriceFrom !== "0") ||
            (selectedPriceTo &&
                selectedPriceTo !== "" &&
                selectedPriceTo !== "500000")
        ) {
            if (data.price_count) {
                count = data.price_count;
                countFound = true;
            }
        }

        // Priorità 2: conteggio per km
        if (
            !countFound &&
            ((selectedKmFrom &&
                selectedKmFrom !== "" &&
                selectedKmFrom !== "0") ||
                (selectedKmTo &&
                    selectedKmTo !== "" &&
                    selectedKmTo !== "500000"))
        ) {
            if (data.km_count) {
                count = data.km_count;
                countFound = true;
            }
        }

        this.updateCount(count);
    }

    /**
     * Resetta le select dipendenti quando cambia una select padre
     * @param {string} changedSelectId - ID della select cambiata
     */
    resetDependentSelects(changedSelectId) {
        const index = this.selectOrder.indexOf(changedSelectId);
        if (index !== -1) {
            for (let i = index + 1; i < this.selectOrder.length; i++) {
                const selectElem = document.getElementById(this.selectOrder[i]);
                if (selectElem) {
                    selectElem.value = "";
                    selectElem.selectedIndex = 0;
                }
                const key = this.selectKeyMap[this.selectOrder[i]];
                if (key) {
                    this.selectedValues[key] = "";
                }
            }
        }
    }

    /**
     * Resetta completamente il form
     */
    resetFormFilters() {
        this.toggleSelectsState(true);

        // Reset hidden inputs
        this.resetHiddenInputs();

        // Reset select elements
        this.resetAllSelects();

        // Reset checkboxes
        this.resetAllCheckboxes();

        // Reset selected values
        this.resetSelectedValues();

        // Aggiorna filtri
        this.updateFilters();
    }

    /**
     * Resetta gli input hidden
     */
    resetHiddenInputs() {
        const elements = {
            price_min_val: "0",
            price_max_val: "500000",
            km_min_val: "0",
            km_max_val: "500000",
        };

        Object.entries(elements).forEach(([suffix, value]) => {
            const element = document.getElementById(`${this.formId}${suffix}`);
            if (element) element.value = value;
        });
    }

    /**
     * Resetta tutte le select
     */
    resetAllSelects() {
        const selectIds = [
            "select-bodytype",
            "select-brand",
            "select-serie",
            "select-model",
            "select-fuel",
            "select-gear",
            "price_min",
            "price_max",
            "km_min",
            "km_max",
        ];

        selectIds.forEach((suffix) => {
            const element = document.getElementById(`${this.formId}${suffix}`);
            if (element) element.value = "";
        });
    }

    /**
     * Resetta tutti i checkbox
     */
    resetAllCheckboxes() {
        document
            .querySelectorAll(`input[name="gruppi[]"]`)
            .forEach((cb) => (cb.checked = false));
        document
            .querySelectorAll(`input[name="warranties[]"]`)
            .forEach((cb) => (cb.checked = false));
    }

    /**
     * Resetta i selected values
     */
    resetSelectedValues() {
        Object.keys(this.selectedValues).forEach((key) => {
            this.selectedValues[key] = "";
        });
    }

    /**
     * Abilita/disabilita tutte le select
     * @param {boolean} disabled - True per disabilitare
     */
    toggleSelectsState(disabled = true) {
        const selectIds = this.selectOrder;

        selectIds.forEach((id) => {
            const select = document.getElementById(id);
            if (select) {
                select.disabled = disabled;
                select.style.cursor = disabled ? "wait" : "";
            }
        });
    }

    /**
     * Aggiorna il conteggio visualizzato
     * @param {number|string} count - Valore del conteggio
     */
    updateCount(count) {
        const countElement = document.getElementById(`${this.formId}conteggio`);
        if (countElement) {
            countElement.textContent = count || "0";
        }
    }

    /**
     * Funzione debounce per limitare le chiamate
     * @param {Function} func - Funzione da debounce
     * @param {number} wait - Tempo di attesa
     * @returns {Function} Funzione debounced
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Distrugge l'istanza e pulisce gli event listeners
     */
    destroy() {
        if (this.resetBtn) {
            this.resetBtn.removeEventListener("click", this.resetFormFilters);
        }
        // Aggiungi qui altri cleanup se necessario
    }
}
